//------------------------------------------------------------------------------
// BFA10104G 共架申込 通信設備
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import {
  PositiveButton,
  StandardButton,
} from '@/components/atoms/Buttons/PositiveButton';
import DisplayField from '@/components/display-field';
import { requiredValue } from '@/components/validateRule';
import {
  renderTextField,
  renderAutocomplete,
} from '@/components/atoms/CustomPart';
import combo from '@/utils/combo-utils';
import wutil from '@/utils/wijmo-utils';
import PoleItem from '@/utils/pole-item';

// 定数
const FORM_NAME = 'BFA10104G-KEIKAN';
const LOGH = 'BFA10104G keikan dailog';

/**
 * 径間編集 ダイアログ
 */
function KeikanDialog({
  initialize,
  handleSubmit,
  initValues,
  idoTypeMap,
  devCodeMap,
  belowStatMap,
  onDialogClose,
}) {

  // プルダウンリスト
  const idoTypeList   = wutil.mapValues(idoTypeMap);
  const devCodeList   = wutil.mapValues(devCodeMap);
  const belowStatList = wutil.mapValues(belowStatMap);

  // 初期処理
  React.useEffect(() => {
    initialize({
      ...initValues,

      // コンボボックスの初期値を設定
      idoType:     combo.findValue(idoTypeList,   initValues?.idoType),
      setubiCode:  combo.findValue(devCodeList,   initValues?.setubiCode),
      senkaJyokyo: combo.findValue(belowStatList, initValues?.senkaJyokyo),
    });
  }, [initValues]);

  // 確定ボタン処理
  const handleCommit = React.useCallback((values) => {
    const result = {
      recordId:     values?.recordId,
      idoType:      values?.idoType?.value,          // 異動種別
      setubiCode:   values?.setubiCode?.value,       // 架設設備コード
      senkaJyokyo:  values?.senkaJyokyo?.value,      // 線下状況
      keikan:       wutil.float(values?.keikan),     // 径間
      tido:         wutil.float(values?.tido),       // 弛度
      rikakuTsn:    wutil.float(values?.rikakuTsn),  // 径間離隔 通信線
      rikakuHkk:    wutil.float(values?.rikakuHkk),  // 径間離隔 電力引込線
    };
    console.info(LOGH, '径間編集確定', result);
    onDialogClose(result);
  }, [onDialogClose]);

  // 取消ボタン処理
  const handleCancel = React.useCallback(() => {
    console.info(LOGH, '径間編集取消');
    onDialogClose(null);
  }, [onDialogClose]);

  //
  // 描画
  //
  return (
    <Dialog
      open={Boolean(initValues)}
      onClose={handleCancel}
      maxWidth="sm" fullWidth
    >
      {/* ヘッダー */}
      <DialogTitle sx={{ py: 1 }}>
        径間編集
      </DialogTitle>
      <Divider sx={{ mb: 1 }}/>

      {/* ボディ */}
      <DialogContent sx={{ px: 2, py: 1 }}>
        <form id="setubi-dialog" noValidate autoComplete="off">

          {/* 参照項目 電柱(1) */}
          <Grid container spacing={1}>

            {/* 地区 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="地区(1)"
                value={initValues?.k1areaName}
              />
            </Grid>

            {/* 線路 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="冠称(1)"
                value={initValues?.k1lineName}
              />
            </Grid>

            {/* 電柱番号 */}
            <Grid item sx={{ width: '10rem' }}>
              <DisplayField label="電柱番号(1)"
                value={PoleItem.text({ poleNo: initValues?.k1poleNo })}
              />
            </Grid>

            {/* 物件番号 */}
            <Grid item sx={{ width: '6rem' }}>
              <DisplayField label="物件番号(1)"
                value={initValues?.k1bukkenNo?.toString()}
              />
            </Grid>
          </Grid>

          {/* 参照項目 電柱(2) */}
          <Grid container spacing={1} sx={{ mt: 1 }}>

            {/* 地区 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="地区(2)"
                value={initValues?.k2areaName}
              />
            </Grid>

            {/* 線路 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="冠称(2)"
                value={initValues?.k2lineName}
              />
            </Grid>

            {/* 電柱番号 */}
            <Grid item sx={{ width: '10rem' }}>
              <DisplayField label="電柱番号(2)"
                value={PoleItem.text({ poleNo: initValues?.k2poleNo })}
              />
            </Grid>

            {/* 物件番号 */}
            <Grid item sx={{ width: '6rem' }}>
              <DisplayField label="物件番号(2)"
                value={initValues?.k2bukkenNo?.toString()}
              />
            </Grid>
          </Grid>

          {/* 編集項目 */}
          <Grid container spacing={2} sx={{ my: 2 }}>

            {/* 異動種別コンボボックス */}
            <Grid item sx={{ width: '12rem' }}>
              <Field name="idoType" label="異動種別"
                component={renderAutocomplete}
                fullWidth size="small" variant="standard"
                options={idoTypeList}
              />
            </Grid>

            {/* 架設設備コンボボックス */}
            <Grid item sx={{ width: '20rem' }}>
              <Field name="setubiCode" label="架設設備"
                component={renderAutocomplete}
                fullWidth size="small" variant="standard"
                required validate={[ requiredValue ]}
                options={devCodeList}
              />
            </Grid>

            {/* 径間 */}
            <Grid item sx={{ width: '6rem' }}>
              <Field name="keikan" label="径間(m)"
                component={renderTextField} type="number"
                fullWidth size="small" variant="standard"
                inputProps={{ step: 0.1, min: 0 }}
              /> 
            </Grid>

            {/* 弛度 */}
            <Grid item sx={{ width: '6rem' }}>
              <Field name="tido" label="弛度(m)"
                component={renderTextField} type="number"
                fullWidth size="small" variant="standard"
                inputProps={{ step: 0.01, min: 0 }}
              /> 
            </Grid>

            {/* 線下状況コンボボックス */}
            <Grid item sx={{ width: '14rem' }}>
              <Field name="senkaJyokyo" label="線下状況"
                component={renderAutocomplete}
                fullWidth size="small" variant="standard"
                options={belowStatList}
              />
            </Grid>

            {/* 径間途中の離隔距離 */}
            <Grid item sx={{ width: '100%', mt: 1 }}>
              <Typography variant="subtitle1">径間途中の離隔</Typography>
              <Grid container spacing={1} sx={{ pt: 1, px: 1 }}>

                {/* 通信線 */}
                <Grid item sx={{ width: '8rem' }}>
                  <Field name="rikakuTsn" label="通信線(m)"
                    component={renderTextField} type="number"
                    fullWidth size="small" variant="standard"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                {/* 電力引込線 */}
                <Grid item sx={{ width: '8rem' }}>
                  <Field name="rikakuHkk" label="電力引込線(m)"
                    component={renderTextField} type="number"
                    fullWidth size="small" variant="standard"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      {/* フッター */}
      <DialogActions>

        {/* 取消ボタン */}
        <StandardButton
          size="large" variant="contained"
          onClick={handleCancel}
        >
          キャンセル
        </StandardButton>

        {/* 確定ボタン */}
        <PositiveButton
          type="submit" form="setubi-dialog"
          size="large" variant="contained"
          onClick={handleSubmit(handleCommit)}
        >
          OK
        </PositiveButton>
      </DialogActions>
    </Dialog>
  );
}

// プロパティ
KeikanDialog.propTypes = {
  initValues:   PropTypes.object,   // ダイアログ初期値
  idoTypeMap:   PropTypes.object,   // 異動種別マップ
  devCodeMap:   PropTypes.object,   // 架設設備マップ
  belowStatMap: PropTypes.object,   // 線下状況マップ

  // フォーム
  initialize: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,

  // ダイアログクローズ処理
  onDialogClose: PropTypes.func.isRequired,
};

// フォーム接続
KeikanDialog = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
})(KeikanDialog);

// エクスポート
export default KeikanDialog;
